export enum IngestionPlatform {
  DOTLOOP = 'DOTLOOP',
  DOCUSIGN = 'DOCUSIGN',
  SKYSLOPE = 'SKYSLOPE',
  SKYSLOPE_FORMS = 'SKYSLOPE_FORMS',
  BROKERMINT = 'BROKERMINT',
  ZIPFORM = 'ZIPFORM',
  KW_COMMAND = 'KW_COMMAND',
}

/**
 * Shared limit for batch loading pipeline investigation reasons.
 * Keep client-side validation and document-service enforcement aligned.
 */
export const MAX_INVESTIGATION_REASON_FOLDERS = 100;

export enum TransactionSide {
  BUY = 'BUY',
  SELL = 'SELL',
  BUY_SELL = 'BUY_SELL',
  UNKNOWN = 'UNKNOWN',
}

export enum NormalizedTransactionStatus {
  PRE_STAGE = 'PRE_STAGE',
  ACTIVE = 'ACTIVE',
  UNDER_CONTRACT = 'UNDER_CONTRACT',
  CLOSED = 'CLOSED',
  CANCELLED = 'CANCELLED',
  PRIVATE_LISTING = 'PRIVATE_LISTING',
  OTHER = 'OTHER',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Maps each normalized status to the raw platform status prefixes
 * that should be matched (case-insensitive, startsWith).
 */
export const NORMALIZED_STATUS_PREFIX_MAP: Record<
  NormalizedTransactionStatus,
  string[]
> = {
  [NormalizedTransactionStatus.PRE_STAGE]: ['pre', 'prospect', 'opportunity'],
  [NormalizedTransactionStatus.ACTIVE]: ['active'],
  [NormalizedTransactionStatus.UNDER_CONTRACT]: ['pending', 'under contract'],
  [NormalizedTransactionStatus.CLOSED]: ['closed', 'sold', 'archived'],
  [NormalizedTransactionStatus.CANCELLED]: [
    'canceled',
    'cancelled',
    'terminated',
    'fell through',
    'withdrawn',
    'withdraw',
  ],
  [NormalizedTransactionStatus.PRIVATE_LISTING]: ['private listing'],
  [NormalizedTransactionStatus.OTHER]: [],
  [NormalizedTransactionStatus.UNKNOWN]: [],
};

/** Returns the normalized status for a raw platform status string. */
export function getNormalizedStatus(
  status: string
): NormalizedTransactionStatus {
  if (!status) return NormalizedTransactionStatus.UNKNOWN;

  const normalized = status.toLowerCase().replace(/_/g, ' ');
  const match = Object.entries(NORMALIZED_STATUS_PREFIX_MAP).find(
    ([, prefixes]) => prefixes.some((p) => normalized.startsWith(p))
  );

  return (match?.[0] as NormalizedTransactionStatus) ??
    NormalizedTransactionStatus.OTHER;
}

export enum IngestionFolderType {
  TRANSACTION = 'TRANSACTION',
  LISTING = 'LISTING',
}

/**
 * Status of the ratified contract for compliance submission tracking.
 *
 * - NO_CONTRACT: no PP contract documents exist in the folder
 * - NOT_RATIFIED: PP docs exist but none are ratified (offer_accepted or fully signed)
 * - NOT_SUBMITTED: ratified PP docs exist but none have been submitted
 * - SUBMITTED: at least one ratified PP doc has been submitted
 */
export enum RatifiedContractStatus {
  NO_CONTRACT = 'no_contract',
  NOT_RATIFIED = 'not_ratified',
  NOT_SUBMITTED = 'not_submitted',
  SUBMITTED = 'submitted',
}
